import AdminLayout from "@/components/layout-admin";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Pencil, Trash2, ShoppingBag, Package } from "lucide-react";

type UpsellItem = {
  id: number;
  organizationId: number;
  name: string;
  description: string | null;
  price: string;
  currency: string;
  category: string;
  isActive: boolean;
  createdAt: Date | null;
};

type UpsellOrder = {
  id: number;
  organizationId: number;
  reservationId: number;
  upsellItemId: number;
  quantity: number;
  unitPrice: string;
  totalPrice: string;
  status: string;
  notes: string | null;
  createdAt: Date | null;
};

type Reservation = {
  id: number;
  guestName: string;
  [key: string]: unknown;
};

const CATEGORIES = [
  { value: "early_checkin", label: "Early Check-in" },
  { value: "late_checkout", label: "Late Checkout" },
  { value: "transport", label: "Transport" },
  { value: "experience", label: "Experience" },
  { value: "amenity", label: "Amenity" },
  { value: "general", label: "General" },
];

const ORDER_STATUSES = [
  { value: "pending", label: "Pending" },
  { value: "confirmed", label: "Confirmed" },
  { value: "delivered", label: "Delivered" },
  { value: "cancelled", label: "Cancelled" },
];

function categoryLabel(cat: string) {
  return CATEGORIES.find((c) => c.value === cat)?.label || cat;
}

function statusVariant(status: string): "default" | "secondary" | "destructive" | "outline" {
  switch (status) {
    case "confirmed":
    case "delivered":
      return "default";
    case "pending":
      return "secondary";
    case "cancelled":
      return "destructive";
    default:
      return "outline";
  }
}

const emptyItemForm = {
  name: "",
  description: "",
  price: "",
  currency: "USD",
  category: "general",
  isActive: true,
};

type ItemFormType = typeof emptyItemForm;

function ItemFormFields({ form, setForm }: { form: ItemFormType; setForm: (f: ItemFormType) => void }) {
  return (
    <div className="space-y-4 py-4">
      <div className="space-y-2">
        <label className="text-sm font-medium">Name</label>
        <Input
          placeholder="e.g. Early Check-in"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          data-testid="input-item-name"
        />
      </div>
      <div className="space-y-2">
        <label className="text-sm font-medium">Description</label>
        <Textarea
          placeholder="Describe this add-on..."
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          data-testid="input-item-description"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className="text-sm font-medium">Price</label>
          <Input
            type="number"
            step="0.01"
            placeholder="0.00"
            value={form.price}
            onChange={(e) => setForm({ ...form, price: e.target.value })}
            data-testid="input-item-price"
          />
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium">Currency</label>
          <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
            <SelectTrigger data-testid="select-item-currency">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="USD">USD</SelectItem>
              <SelectItem value="EUR">EUR</SelectItem>
              <SelectItem value="GBP">GBP</SelectItem>
              <SelectItem value="MXN">MXN</SelectItem>
              <SelectItem value="COP">COP</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="space-y-2">
        <label className="text-sm font-medium">Category</label>
        <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
          <SelectTrigger data-testid="select-item-category">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {CATEGORIES.map((c) => (
              <SelectItem key={c.value} value={c.value}>
                {c.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-center gap-3">
        <Switch
          checked={form.isActive}
          onCheckedChange={(checked) => setForm({ ...form, isActive: checked })}
          data-testid="switch-item-active"
        />
        <label className="text-sm font-medium">Active</label>
      </div>
    </div>
  );
}

export default function AdminUpsells() {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("catalog");

  const { data: items, isLoading: itemsLoading } = useQuery<UpsellItem[]>({
    queryKey: ["/api/admin/upsell-items"],
  });

  const { data: orders, isLoading: ordersLoading } = useQuery<UpsellOrder[]>({
    queryKey: ["/api/admin/upsell-orders"],
  });

  const { data: reservations } = useQuery<Reservation[]>({
    queryKey: ["/api/admin/reservations"],
  });

  const [createItemOpen, setCreateItemOpen] = useState(false);
  const [editItem, setEditItem] = useState<UpsellItem | null>(null);
  const [itemForm, setItemForm] = useState(emptyItemForm);

  const [createOrderOpen, setCreateOrderOpen] = useState(false);
  const [orderForm, setOrderForm] = useState({
    reservationId: "",
    upsellItemId: "",
    quantity: "1",
  });

  const createItemMutation = useMutation({
    mutationFn: (data: typeof emptyItemForm) =>
      apiRequest("POST", "/api/admin/upsell-items", {
        ...data,
        price: data.price,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/upsell-items"] });
      setCreateItemOpen(false);
      setItemForm(emptyItemForm);
      toast({ title: "Item created" });
    },
    onError: () => toast({ title: "Failed to create item", variant: "destructive" }),
  });

  const updateItemMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: typeof emptyItemForm }) =>
      apiRequest("PUT", `/api/admin/upsell-items/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/upsell-items"] });
      setEditItem(null);
      toast({ title: "Item updated" });
    },
    onError: () => toast({ title: "Failed to update item", variant: "destructive" }),
  });

  const deleteItemMutation = useMutation({
    mutationFn: (id: number) => apiRequest("DELETE", `/api/admin/upsell-items/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/upsell-items"] });
      toast({ title: "Item deleted" });
    },
    onError: () => toast({ title: "Failed to delete item", variant: "destructive" }),
  });

  const createOrderMutation = useMutation({
    mutationFn: (data: {
      reservationId: number;
      upsellItemId: number;
      quantity: number;
      unitPrice: string;
      totalPrice: string;
      status: string;
    }) => apiRequest("POST", "/api/admin/upsell-orders", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/upsell-orders"] });
      setCreateOrderOpen(false);
      setOrderForm({ reservationId: "", upsellItemId: "", quantity: "1" });
      toast({ title: "Order created" });
    },
    onError: () => toast({ title: "Failed to create order", variant: "destructive" }),
  });

  const deleteOrderMutation = useMutation({
    mutationFn: (id: number) => apiRequest("DELETE", `/api/admin/upsell-orders/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/upsell-orders"] });
      toast({ title: "Order deleted" });
    },
    onError: () => toast({ title: "Failed to delete order", variant: "destructive" }),
  });

  const handleEditItem = (item: UpsellItem) => {
    setEditItem(item);
    setItemForm({
      name: item.name,
      description: item.description || "",
      price: item.price,
      currency: item.currency,
      category: item.category,
      isActive: item.isActive,
    });
  };

  const handleCreateOrder = () => {
    const selectedItem = items?.find((i) => i.id === Number(orderForm.upsellItemId));
    if (!selectedItem) return;
    const qty = Number(orderForm.quantity) || 1;
    const unitPrice = selectedItem.price;
    const totalPrice = (parseFloat(unitPrice) * qty).toFixed(2);
    createOrderMutation.mutate({
      reservationId: Number(orderForm.reservationId),
      upsellItemId: Number(orderForm.upsellItemId),
      quantity: qty,
      unitPrice,
      totalPrice,
      status: "pending",
    });
  };

  const selectedOrderItem = items?.find((i) => i.id === Number(orderForm.upsellItemId));
  const orderQuantity = Number(orderForm.quantity) || 0;
  const calculatedTotal = selectedOrderItem
    ? (parseFloat(selectedOrderItem.price) * orderQuantity).toFixed(2)
    : "0.00";

  return (
    <AdminLayout>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-display font-bold" data-testid="text-page-title">
            Upsells & Add-ons
          </h1>
          <p className="text-muted-foreground mt-2">
            Manage your catalog of extras and track guest orders.
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList data-testid="tabs-upsells">
          <TabsTrigger value="catalog" data-testid="tab-catalog">
            Catalog
          </TabsTrigger>
          <TabsTrigger value="orders" data-testid="tab-orders">
            Orders
          </TabsTrigger>
        </TabsList>

        <TabsContent value="catalog" className="mt-6">
          <div className="flex justify-end mb-4">
            <Dialog open={createItemOpen} onOpenChange={setCreateItemOpen}>
              <DialogTrigger asChild>
                <Button
                  data-testid="button-add-item"
                  onClick={() => setItemForm(emptyItemForm)}
                >
                  <Plus className="w-4 h-4 mr-2" /> Add Item
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create Upsell Item</DialogTitle>
                </DialogHeader>
                <ItemFormFields form={itemForm} setForm={setItemForm} />
                <Button
                  className="w-full"
                  onClick={() => createItemMutation.mutate(itemForm)}
                  disabled={createItemMutation.isPending || !itemForm.name || !itemForm.price}
                  data-testid="button-submit-item"
                >
                  {createItemMutation.isPending ? "Creating..." : "Create Item"}
                </Button>
              </DialogContent>
            </Dialog>
          </div>

          {itemsLoading && (
            <div className="text-center py-12 text-muted-foreground">Loading items...</div>
          )}

          {!itemsLoading && (!items || items.length === 0) && (
            <div className="text-center py-12" data-testid="text-empty-items">
              <Package className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                No upsell items yet. Add your first item to get started.
              </p>
            </div>
          )}

          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {items?.map((item) => (
              <Card key={item.id} data-testid={`card-item-${item.id}`}>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-primary/10 rounded-xl flex-shrink-0">
                      <ShoppingBag className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3
                        className="text-lg font-bold font-display"
                        data-testid={`text-item-name-${item.id}`}
                      >
                        {item.name}
                      </h3>
                      {item.description && (
                        <p
                          className="text-sm text-muted-foreground mt-1 line-clamp-2"
                          data-testid={`text-item-description-${item.id}`}
                        >
                          {item.description}
                        </p>
                      )}
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <span
                          className="text-sm font-semibold"
                          data-testid={`text-item-price-${item.id}`}
                        >
                          {item.currency} {parseFloat(item.price).toFixed(2)}
                        </span>
                        <Badge variant="outline" data-testid={`badge-item-category-${item.id}`}>
                          {categoryLabel(item.category)}
                        </Badge>
                        <Badge
                          variant={item.isActive ? "default" : "secondary"}
                          data-testid={`badge-item-status-${item.id}`}
                        >
                          {item.isActive ? "Active" : "Inactive"}
                        </Badge>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 mt-4 pt-4 border-t justify-end">
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => handleEditItem(item)}
                      data-testid={`button-edit-item-${item.id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          size="icon"
                          variant="ghost"
                          data-testid={`button-delete-item-${item.id}`}
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Item</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to delete "{item.name}"? This action cannot be
                            undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel data-testid="button-cancel-delete-item">
                            Cancel
                          </AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => deleteItemMutation.mutate(item.id)}
                            data-testid="button-confirm-delete-item"
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <Dialog
            open={!!editItem}
            onOpenChange={(open) => {
              if (!open) setEditItem(null);
            }}
          >
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Edit Upsell Item</DialogTitle>
              </DialogHeader>
              <ItemFormFields form={itemForm} setForm={setItemForm} />
              <Button
                className="w-full"
                onClick={() => {
                  if (editItem) updateItemMutation.mutate({ id: editItem.id, data: itemForm });
                }}
                disabled={updateItemMutation.isPending || !itemForm.name || !itemForm.price}
                data-testid="button-update-item"
              >
                {updateItemMutation.isPending ? "Updating..." : "Update Item"}
              </Button>
            </DialogContent>
          </Dialog>
        </TabsContent>

        <TabsContent value="orders" className="mt-6">
          <div className="flex justify-end mb-4">
            <Dialog open={createOrderOpen} onOpenChange={setCreateOrderOpen}>
              <DialogTrigger asChild>
                <Button
                  data-testid="button-add-order"
                  onClick={() =>
                    setOrderForm({ reservationId: "", upsellItemId: "", quantity: "1" })
                  }
                >
                  <Plus className="w-4 h-4 mr-2" /> Add Order
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create Upsell Order</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Reservation</label>
                    <Select
                      value={orderForm.reservationId}
                      onValueChange={(v) => setOrderForm({ ...orderForm, reservationId: v })}
                    >
                      <SelectTrigger data-testid="select-order-reservation">
                        <SelectValue placeholder="Select reservation" />
                      </SelectTrigger>
                      <SelectContent>
                        {reservations?.map((r) => (
                          <SelectItem key={r.id} value={String(r.id)}>
                            {r.guestName} (#{r.id})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Upsell Item</label>
                    <Select
                      value={orderForm.upsellItemId}
                      onValueChange={(v) => setOrderForm({ ...orderForm, upsellItemId: v })}
                    >
                      <SelectTrigger data-testid="select-order-item">
                        <SelectValue placeholder="Select item" />
                      </SelectTrigger>
                      <SelectContent>
                        {items
                          ?.filter((i) => i.isActive)
                          .map((i) => (
                            <SelectItem key={i.id} value={String(i.id)}>
                              {i.name} ({i.currency} {parseFloat(i.price).toFixed(2)})
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Quantity</label>
                    <Input
                      type="number"
                      min="1"
                      value={orderForm.quantity}
                      onChange={(e) => setOrderForm({ ...orderForm, quantity: e.target.value })}
                      data-testid="input-order-quantity"
                    />
                  </div>
                  {selectedOrderItem && orderQuantity > 0 && (
                    <div
                      className="text-sm text-muted-foreground"
                      data-testid="text-order-total-preview"
                    >
                      Total: {selectedOrderItem.currency} {calculatedTotal}
                    </div>
                  )}
                </div>
                <Button
                  className="w-full"
                  onClick={handleCreateOrder}
                  disabled={
                    createOrderMutation.isPending ||
                    !orderForm.reservationId ||
                    !orderForm.upsellItemId
                  }
                  data-testid="button-submit-order"
                >
                  {createOrderMutation.isPending ? "Creating..." : "Create Order"}
                </Button>
              </DialogContent>
            </Dialog>
          </div>

          {ordersLoading && (
            <div className="text-center py-12 text-muted-foreground">Loading orders...</div>
          )}

          {!ordersLoading && (!orders || orders.length === 0) && (
            <div className="text-center py-12" data-testid="text-empty-orders">
              <ShoppingBag className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No upsell orders yet.</p>
            </div>
          )}

          {orders && orders.length > 0 && (
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Guest</TableHead>
                      <TableHead>Item</TableHead>
                      <TableHead>Qty</TableHead>
                      <TableHead>Total</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {orders.map((order) => {
                      const item = items?.find((i) => i.id === order.upsellItemId);
                      const reservation = reservations?.find(
                        (r) => r.id === order.reservationId,
                      );
                      return (
                        <TableRow key={order.id} data-testid={`row-order-${order.id}`}>
                          <TableCell data-testid={`text-order-guest-${order.id}`}>
                            {reservation?.guestName || `Reservation #${order.reservationId}`}
                          </TableCell>
                          <TableCell data-testid={`text-order-item-${order.id}`}>
                            {item?.name || `Item #${order.upsellItemId}`}
                          </TableCell>
                          <TableCell data-testid={`text-order-quantity-${order.id}`}>
                            {order.quantity}
                          </TableCell>
                          <TableCell data-testid={`text-order-total-${order.id}`}>
                            {parseFloat(order.totalPrice).toFixed(2)}
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant={statusVariant(order.status)}
                              data-testid={`badge-order-status-${order.id}`}
                            >
                              {order.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  data-testid={`button-delete-order-${order.id}`}
                                >
                                  <Trash2 className="w-4 h-4 text-destructive" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete Order</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Are you sure you want to delete this order? This action cannot
                                    be undone.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel data-testid="button-cancel-delete-order">
                                    Cancel
                                  </AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => deleteOrderMutation.mutate(order.id)}
                                    data-testid="button-confirm-delete-order"
                                  >
                                    Delete
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </AdminLayout>
  );
}
